import React, { useState, useMemo } from 'react';
import {
  Alert,
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Modal,
  Radio,
  Row,
  Select,
  Space,
  Table,
  Typography,
} from 'antd';
import { TranslationOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import useFetch from '../../../../../hooks/useFetch';
import { FetchApiInterface } from '../../../../../interfaces/FetchApi.interface';
import { AxiosResponse } from 'axios';
import { ResponseInterface } from '../../../../../interfaces/Response.interface';

const { TextArea } = Input;
const { Text } = Typography;

const VALID_STATUSES = ['recibido a tiempo', 'recibido tarde'];

// TODO: replace with API call
const MOCK_TRANSLATORS = [
  { key: '1', name: 'Roberto Paz', country: 'Ecuador', languages: 'Español/Inglés/Francés', currentLoad: 325, monthTranslations: 680, fyTranslations: 2500 },
  { key: '2', name: 'Julio Iglesias', country: 'Ecuador', languages: 'Español/Inglés', currentLoad: 50, monthTranslations: 100, fyTranslations: 250 },
  { key: '3', name: 'Carmen Martínez', country: 'Ecuador', languages: 'Español/Inglés', currentLoad: 150, monthTranslations: 250, fyTranslations: 850 },
];


interface RequestTranslationProps {
  selectedItems: Array<any>;
  selectedRows: Array<any>;
}

const RequestTranslation: React.FC<RequestTranslationProps> = ({ selectedItems, selectedRows }) => {
  const [open, setOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedTranslatorKey, setSelectedTranslatorKey] = useState<string | null>(null);
  const [formValues, setFormValues] = useState<any>({});
  const [translatorLanguages, setTranslatorLanguages] = useState<Array<{ tlaId: number; tlaName: string; tlaCode: string }>>([]);
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const { fetchApi } = useFetch();

  const getTranslatorLanguages = () => {
    const dataSend: FetchApiInterface = {
      url: `/language/translator_language`,
      method: 'GET',
    };
    fetchApi(dataSend).then(
      ({ data }: AxiosResponse<ResponseInterface>) => {
        if (data.status === 'success') {
          setTranslatorLanguages(data?.data || []);
        }
      }
    ).catch(() => {});
  };

  const validItems = useMemo(
    () =>
      selectedRows.filter((item) =>
        VALID_STATUSES.some((s) => item.status?.toLowerCase().includes(s.toLowerCase()))
      ),
    [selectedRows]
  );

  const invalidCount = selectedRows.length - validItems.length;

  const translatorColumns = [
    {
      title: '',
      key: 'select',
      width: 40,
      render: (_: any, record: any) => (
        <Radio
          checked={selectedTranslatorKey === record.key}
          onChange={() => setSelectedTranslatorKey(record.key)}
        />
      ),
    },
    { title: 'Traductor', dataIndex: 'name', key: 'name' },
    { title: 'País', dataIndex: 'country', key: 'country' },
    { title: 'Idiomas', dataIndex: 'languages', key: 'languages' },
    { title: 'Carga de trabajo actual', dataIndex: 'currentLoad', key: 'currentLoad' },
    { title: 'Traducciones mes', dataIndex: 'monthTranslations', key: 'monthTranslations' },
    { title: 'Traducciones FY', dataIndex: 'fyTranslations', key: 'fyTranslations' },
  ];

  const communicationColumns = [
    { title: 'Nro', key: 'nro', render: (_: any, __: any, index: number) => index + 1 },
    { title: 'SC', dataIndex: 'ascScNumber', key: 'ascScNumber' },
    { title: 'Nombre SC', dataIndex: 'ascScName', key: 'ascScName' },
    { title: 'Solicitante', dataIndex: 'lastModifiedBy', key: 'lastModifiedBy' },
    {
      title: 'Traductor',
      key: 'translator',
      render: () => MOCK_TRANSLATORS.find((tr) => tr.key === selectedTranslatorKey)?.name || '-',
    },
    {
      title: 'Idioma Origen',
      key: 'sourceLanguage',
      render: () => formValues.sourceLanguage || '-',
    },
    {
      title: 'Idioma Destino',
      key: 'targetLanguage',
      render: () => formValues.targetLanguage || '-',
    },
    {
      title: 'Fecha entrega',
      key: 'deliveryDate',
      render: () =>
        formValues.deliveryDate ? moment(formValues.deliveryDate).format('DD-MM-YYYY') : '-',
    },
  ];

  const handleOpen = () => {
    setSelectedTranslatorKey(null);
    setFormValues({});
    form.resetFields();
    form.setFieldsValue({
      translatorType: 'country',
      requestDate: moment(),
    });
    getTranslatorLanguages();
    setOpen(true);
  };

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      setFormValues(values);
      setOpen(false);
      setConfirmOpen(true);
    });
  };

  const handleConfirm = () => {
    // TODO: API call to send translation request
    setConfirmOpen(false);
  };

  const footer = (
    <Space wrap>
      <Button onClick={() => setOpen(false)}>{t('cancel') || 'Cancelar'}</Button>
      <Button type='default'>{t('recoverSend') || '*Recuperar envío'}</Button>
      <Button type='default'>{t('resend') || '*Reenviar'}</Button>
      <Button type='primary' icon={<TranslationOutlined />} onClick={handleSubmit}>
        {t('translateWithAIAndSend') || 'Traducir con IA y Enviar a traductor'}
      </Button>
    </Space>
  );

  return (
    <>
      <Button
        type='primary'
        disabled={selectedItems.length <= 0}
        icon={<TranslationOutlined />}
        onClick={handleOpen}
      >
        {t('requestTranslation')}
      </Button>

      <Modal
        centered
        open={open}
        title={t('requestTranslation') || 'Solicitud de traducción'}
        onCancel={() => setOpen(false)}
        width={960}
        footer={footer}
      >
        {invalidCount > 0 && (
          <Alert
            type='warning'
            showIcon
            message={`${invalidCount} comunicación(es) excluida(s) por no estar en estado "Recibido a tiempo" o "Recibido tarde".`}
            style={{ marginBottom: 16 }}
          />
        )}

        <Form
          form={form}
          layout='vertical'
          onValuesChange={(changed, all) => {
            if (changed.translationType2 === 'SC-SP') {
              const updated = { ...all, sourceLanguage: 'Español', targetLanguage: 'Inglés' };
              form.setFieldsValue({ sourceLanguage: 'Español', targetLanguage: 'Inglés' });
              setFormValues(updated);
            } else if (changed.translationType2 === 'SP-SC') {
              const updated = { ...all, sourceLanguage: 'Inglés', targetLanguage: 'Español' };
              form.setFieldsValue({ sourceLanguage: 'Inglés', targetLanguage: 'Español' });
              setFormValues(updated);
            } else {
              setFormValues(all);
            }
          }}
        >
          <Form.Item label='Tipo de traductor' name='translatorType'>
            <Select style={{ width: 220 }}>
              <Select.Option value='country'>Traductor del país</Select.Option>
              <Select.Option value='other'>Traductor de otro país</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item label='Traductores disponibles'>
            <Table
              size='small'
              dataSource={MOCK_TRANSLATORS}
              columns={translatorColumns}
              pagination={false}
              rowKey='key'
            />
          </Form.Item>

          <Form.Item label={t('translationType')} name='translationType2'>
            <Radio.Group>
              <Radio value='SC-SP'>SC-SP</Radio>
              <Radio value='SP-SC'>SP-SC</Radio>
            </Radio.Group>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label='Idioma Inicial'
                name='sourceLanguage'
                rules={[{ required: true, message: 'Seleccione idioma inicial' }]}
              >
                <Select>
                  {translatorLanguages.map((l) => (
                    <Select.Option key={l.tlaId} value={l.tlaName}>{l.tlaName}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label='Idioma Final'
                name='targetLanguage'
                rules={[{ required: true, message: 'Seleccione idioma final' }]}
              >
                <Select>
                  {translatorLanguages.map((l) => (
                    <Select.Option key={l.tlaId} value={l.tlaName}>{l.tlaName}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label='Fecha solicitud' name='requestDate'>
                <DatePicker disabled format='DD/MM/YYYY' style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label='Fecha entrega'
                name='deliveryDate'
                rules={[{ required: true, message: 'Seleccione fecha de entrega' }]}
              >
                <DatePicker
                  format='DD/MM/YYYY'
                  style={{ width: '100%' }}
                  disabledDate={(current) => current && current <= moment().endOf('day')}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label='Comentarios para el traductor' name='comments'>
            <TextArea rows={3} />
          </Form.Item>

          <Form.Item label={`Lista de comunicaciones: (${validItems.length})`}>
            <Table
              size='small'
              dataSource={validItems}
              columns={communicationColumns}
              pagination={false}
              rowKey='assId'
              footer={() => (
                <Text strong>Total comunicaciones a traducir: {validItems.length}</Text>
              )}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        centered
        open={confirmOpen}
        title='Confirmar solicitud'
        onCancel={() => setConfirmOpen(false)}
        footer={
          <Space>
            <Button onClick={() => { setConfirmOpen(false); setOpen(true); }}>Atrás</Button>
            <Button type='primary' onClick={handleConfirm}>Aceptar</Button>
          </Space>
        }
      >
        <p>Se enviará la siguiente solicitud de traducción:</p>
        <div style={{ border: '1px solid #1677ff', borderRadius: 4, padding: '12px 16px', marginBottom: 16 }}>
          <p style={{ margin: '4px 0' }}><strong>Traductor:</strong> {MOCK_TRANSLATORS.find((tr) => tr.key === selectedTranslatorKey)?.name || 'sin seleccionar'}</p>
          <p style={{ margin: '4px 0' }}><strong>Idioma inicial:</strong> {formValues.sourceLanguage || '-'}</p>
          <p style={{ margin: '4px 0' }}><strong>Idioma final:</strong> {formValues.targetLanguage || '-'}</p>
          <p style={{ margin: '4px 0' }}><strong>Total comunicaciones:</strong> {validItems.length}</p>
          <p style={{ margin: '4px 0' }}><strong>Fecha de solicitud:</strong> {moment().format('DD/MM/YYYY')}</p>
          <p style={{ margin: '4px 0' }}><strong>Fecha de entrega:</strong> {formValues.deliveryDate ? moment(formValues.deliveryDate).format('DD/MM/YYYY') : '-'}</p>
          {formValues.comments && (
            <p style={{ margin: '4px 0' }}><strong>Comentarios para el traductor:</strong> {formValues.comments}</p>
          )}
        </div>
        <p>¿Está seguro que desea realizar esta acción?</p>
      </Modal>
    </>
  );
};

export default RequestTranslation;
